import React, { useState } from 'react';

// Föreningens styrelsekonton i Hyria admin, med "Lägg till styrelseadmin".
// 2026-10-13: Brf Erikslust stod utan styrelsekonto och Hyria hade ingen väg att
// skapa ett — "Glömt lösenord" hittade ingen användare. Kontot skapas av servern
// (addBrfAdmin); personen väljer själv lösenord via "Glömt lösenord".
// Ta bort (removeBrfAdmin): ett konto med e-post stängs av, ett BankID-konto
// behåller sitt ägarkonto och förlorar bara styrelserollen. Föreningens sista
// styrelseadmin kan inte tas bort — servern spärrar det också.

const isEmail = (email) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

const nameOf = (a) => [a.firstName, a.lastName].filter(Boolean).join(' ') || a.email || 'Namn saknas';

export default function BoardAdminsPanel({ admins = [], onAdd, onRemove }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [confirming, setConfirming] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');
  const onlyOne = admins.length <= 1;

  const remove = async (a) => {
    setRemoveError('');
    setRemoving(true);
    try {
      await onRemove(a);
      setConfirming(null);
    } catch (err) {
      setRemoveError(err?.message || 'Styrelseadmin kunde inte tas bort. Försök igen.');
    } finally {
      setRemoving(false);
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setDone('');
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = form.email.trim().toLowerCase();
    if (!firstName || !lastName || !isEmail(email)) {
      setError('Fyll i förnamn, efternamn och en giltig e-postadress.');
      return;
    }
    setSaving(true);
    try {
      await onAdd({ firstName, lastName, email });
      setDone(`Kontot för ${email} är klart. Be personen klicka på "Glömt lösenord" på inloggningssidan för att välja lösenord.`);
      setForm({ firstName: '', lastName: '', email: '' });
      setOpen(false);
    } catch (err) {
      setError(err?.message || 'Kontot kunde inte skapas. Försök igen.');
    } finally {
      setSaving(false);
    }
  };

  const input = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#62B8E9] focus:outline-none';

  return (
    <div className="mt-8">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="font-semibold text-gray-900">Styrelseadministratörer</h3>
        {!open && (
          <button
            type="button"
            onClick={() => {
              setOpen(true);
              setDone('');
            }}
            className="rounded-lg bg-[#62B8E9] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#4A9FD1]"
          >
            Lägg till styrelseadmin
          </button>
        )}
      </div>

      {admins.length === 0 ? (
        <div role="alert" className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Föreningen har inget styrelsekonto. Ingen kan logga in som styrelse förrän ett konto läggs till.
        </div>
      ) : (
        <>
          <ul className="mb-3 divide-y divide-gray-100 rounded-lg border border-gray-100">
            {admins.map((a) => (
              <li key={a.id} className="px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-gray-900">{nameOf(a)}</span>
                  <span className="flex items-center gap-3">
                    <span className="text-gray-600">{a.email}</span>
                    {onRemove && (
                      <button
                        type="button"
                        onClick={() => {
                          setConfirming(a.id);
                          setRemoveError('');
                        }}
                        disabled={onlyOne || removing}
                        aria-label={`Ta bort ${nameOf(a)}`}
                        title={onlyOne ? 'Lägg till en ny styrelseadmin först' : undefined}
                        className="rounded-lg border border-red-200 px-2 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Ta bort
                      </button>
                    )}
                  </span>
                </div>
                {confirming === a.id && (
                  <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-sm text-red-800">
                      Ta bort {nameOf(a)} som styrelseadmin? Personen kan inte längre logga in som styrelse.
                    </p>
                    {removeError && <p className="mt-2 text-sm text-red-700">{removeError}</p>}
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onClick={() => remove(a)}
                        disabled={removing}
                        className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
                      >
                        {removing ? 'Tar bort …' : 'Ja, ta bort'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setConfirming(null);
                          setRemoveError('');
                        }}
                        aria-label="Avbryt borttagning"
                        className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-700 transition hover:bg-white"
                      >
                        Avbryt
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {onRemove && onlyOne && (
            <p className="mb-3 text-xs text-gray-500">Den enda styrelseadmin kan inte tas bort. Lägg till en ny först.</p>
          )}
        </>
      )}

      {done && (
        <p role="status" className="mb-3 rounded-lg bg-green-50 p-3 text-sm text-green-800">
          {done}
        </p>
      )}

      {open && (
        <form onSubmit={submit} className="rounded-lg border border-gray-200 p-4" noValidate>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <label className="text-sm text-gray-700">
              <span className="mb-1 block">Förnamn</span>
              <input className={input} value={form.firstName} onChange={set('firstName')} aria-label="Förnamn" />
            </label>
            <label className="text-sm text-gray-700">
              <span className="mb-1 block">Efternamn</span>
              <input className={input} value={form.lastName} onChange={set('lastName')} aria-label="Efternamn" />
            </label>
            <label className="text-sm text-gray-700">
              <span className="mb-1 block">E-post</span>
              <input className={input} type="email" value={form.email} onChange={set('email')} aria-label="E-post" />
            </label>
          </div>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          <div className="mt-4 flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-[#62B8E9] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#4A9FD1] disabled:opacity-50"
            >
              {saving ? 'Skapar …' : 'Skapa konto'}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setError('');
              }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
            >
              Avbryt
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
